/**
 * MasterListCard — wraps `<MasterRow>` instances in one bordered card
 * with internal `border-top` separators between rows.
 *
 * Mirrors prototype's `.cust-list` / `.vend-list` shape — borders are
 * BETWEEN rows, not per-row, matching the grouped-card aesthetic.
 *
 * The optional `footer` renders the prototype's `.list-foot` summary
 * strip inside the same card; pass `[]` to skip it.
 */
import * as React from "react";
import { cn } from "@flint/ui";

export interface MasterListCardProps {
  children: React.ReactNode;
  /**
   * Optional column-header row, shown above the rows (desktop only, where the
   * card-row's full grid is stable). Pass a grid that matches the row's `lg`
   * column template with label spans; this wrapper supplies the `bg-raised`
   * strip + the standard header text style (matching the table-grid headers).
   */
  header?: React.ReactNode;
  /**
   * Optional footer summary strip — `[label, value]` tuples with optional
   * `accent` to color the value. Pass an empty array to omit the strip.
   */
  footer?: Array<{ label: string; value: React.ReactNode; accent?: "default" | "bad" }>;
  /**
   * Optional second strip below the summary footer — typically a
   * `<ListPagination>` instance with prev/next chrome and a
   * "Showing X–Y of N · sorted by …" caption. Mirrors the two-strip
   * pattern from `invoices/list/invoice-table.tsx`.
   */
  pagination?: React.ReactNode;
}

export function MasterListCard({ children, header, footer, pagination }: MasterListCardProps) {
  return (
    <div className="bg-paper border border-line rounded-md shadow-sm overflow-hidden">
      {header ? (
        <div className="hidden lg:block bg-raised border-b border-line text-xs font-medium uppercase tracking-wide text-muted">
          {header}
        </div>
      ) : null}
      <div className="[&>a:not(:first-child)]:border-t [&>a]:border-line">{children}</div>
      {footer && footer.length > 0 ? (
        <div className="flex flex-wrap gap-x-5 gap-y-2 px-4 md:px-5 py-2.5 bg-raised border-t border-line text-xs text-muted">
          {footer.map((s, i) => (
            <span key={i} className="inline-flex items-center gap-1.5">
              {s.label}
              <strong
                className={cn(
                  "font-mono tabular-nums font-bold",
                  s.accent === "bad" ? "text-bad" : "text-ink",
                )}
              >
                {s.value}
              </strong>
            </span>
          ))}
        </div>
      ) : null}
      {pagination ? <div className="border-t border-line">{pagination}</div> : null}
    </div>
  );
}
